import React from "react";
import { Sparkles, Users, CalendarDays, Clock, Building2 } from "lucide-react";
import { useAuth } from "../../../contexts/AuthContext";
import { useDashTheme } from "../hooks/useDashTheme";
import { PageShell } from "../components/PageShell";
import { Card } from "../components/primitives";
import { EmptyState } from "../components/DataTable";
import { TabPanel } from "../components/Tabs";
import AntDropdown from "../../../components/UI/AntDropdown";
import AIUsageBanner from "../../../components/AIUsageBanner/AIUsageBanner";
import { HourlyInsightsV2 } from "../components/HourlyInsightsV2";
import DepartmentReportPanel from "../../DailySummary/DepartmentReport/DepartmentReportPanel";
import { canUserAccessDeptReport } from "../../DailySummary/DepartmentReport/deptReportGating";
import { SERVER_URL } from "../../../utils/serverConfig";
import { WorkReport } from "../components/workReport";

/* Work summary.

   The daily tab is a written report, not a dashboard: a numbered list of what
   the person actually did, in sentences, with the detail folded away until
   someone asks for it. The reader is usually an executive who wants the answer
   in twenty seconds and the evidence only if something looks off.

   The KPI/chart components this tab used to render (WorkMetricsDashboard,
   AppUsagePanel, WorkSummaryRenderer) are untouched on disk — they are simply
   no longer mounted here, so restoring them is a one-line change. */

const fmt = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
};

export default function WorkSummaryV2() {
  const t = useDashTheme();
  const { user } = useAuth();

  const [tab, setTab] = React.useState("daily");
  const [member, setMember] = React.useState(null);

  const [accessibleUsers, setAccessibleUsers] = React.useState([]);
  const [loadingUsers, setLoadingUsers] = React.useState(true);
  const [hourlyUserId, setHourlyUserId] = React.useState(null);
  const [hourlyDate, setHourlyDate] = React.useState(() => fmt(new Date()));

  React.useEffect(() => {
    (async () => {
      setLoadingUsers(true);
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${SERVER_URL}/api/v1/saved-summaries?limit=1`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await res.json();
        if (data?.success && data.data?.accessibleUsers) {
          setAccessibleUsers(data.data.accessibleUsers);
          const me = data.data.accessibleUsers.find((u) => u._id === user?._id) || data.data.accessibleUsers[0];
          if (me) { setHourlyUserId(me._id); setMember((cur) => cur || me._id); }
        }
      } catch (_) {} finally { setLoadingUsers(false); }
    })();
  }, [user]);

  const members = React.useMemo(() => accessibleUsers.map((u) => ({
    value: u._id || u.id,
    label: `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email || "Member",
    sub: u.position || u.email,
  })), [accessibleUsers]);

  const selectedMember = members.find((m) => m.value === member);
  const showDept = canUserAccessDeptReport(user);

  const memberPicker = (
    <div style={{ minWidth: 210 }}>
      <AntDropdown
        value={member || undefined} options={members} onChange={(v) => setMember(v || null)}
        placeholder={loadingUsers ? "Loading…" : `Choose someone (${members.length})`}
        allowClear getPopupContainer={(node) => node.parentNode}
      />
    </div>
  );

  const tabs = [
    { key: "daily", label: "Daily report", icon: <CalendarDays size={15} /> },
    { key: "hourly", label: "Hourly insights", icon: <Clock size={15} /> },
    ...(showDept ? [{ key: "department", label: "Department report", icon: <Building2 size={15} /> }] : []),
  ];

  return (
    <PageShell
      eyebrow="AI · Work intelligence"
      title="Work summary"
      subtitle="What each person actually worked on, written out day by day"
      icon={<Sparkles size={22} />}
      tabs={tabs} activeTab={tab} onTabChange={setTab} tabLayoutId="workShellTab"
    >
      <AIUsageBanner />

      <TabPanel activeKey={tab} tabKey="daily">
        {/* The member picker lives inside the report's control bar rather than in
            a card of its own — one 52px row instead of three stacked bands. */}
        {member ? (
          <WorkReport
            key={member}
            userId={member}
            memberLabel={selectedMember?.label}
            memberControl={memberPicker}
          />
        ) : (
          <div>
            <Card padding={0} style={{ marginBottom: t.s[4] }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: `${t.s[3]}px ${t.s[4]}px`, flexWrap: "wrap" }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: t.c.textTertiary, fontSize: t.font.size.sm, fontWeight: 500 }}>
                  <Users size={14} /> Who
                </span>
                <div style={{ minWidth: 250 }}>{memberPicker}</div>
              </div>
            </Card>
            <Card>
              <EmptyState
                icon={<Users size={22} />}
                title="Choose someone to see their day"
                description="Pick a team member above. Their hours appear straight away; the write-up of what they worked on is one click after that."
              />
            </Card>
          </div>
        )}
      </TabPanel>

      <TabPanel activeKey={tab} tabKey="hourly">
        <HourlyInsightsV2
          accessibleUsers={accessibleUsers}
          loadingUsers={loadingUsers}
          selectedUserId={hourlyUserId}
          setSelectedUserId={setHourlyUserId}
          selectedDate={hourlyDate}
          setSelectedDate={setHourlyDate}
        />
      </TabPanel>

      {showDept && (
        <TabPanel activeKey={tab} tabKey="department">
          <Card padding={t.s[2]}>
            <DepartmentReportPanel />
          </Card>
        </TabPanel>
      )}
    </PageShell>
  );
}
